import { useQuery } from '@apollo/client';
import { t, Trans } from '@lingui/macro';
import React, { FC, memo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';

import AdminMessageController from 'Components/AdminMessage/AdminMessageController';
import AndroidBanner from 'Components/AndroidBanner/AndroidBanner';
import { useCurrentUser } from 'Components/CurrentUser/CurrentUser';
import RedirectWithStatus from 'common/CustomRoutes/RedirectWithStatus';
import PageView from 'common/PageView/PageView';
import { isWindow } from 'common/helpers/helpers';
import config from 'config/config';
import routes from 'utils/routeTranslator';

import {
  allCategoriesQuery,
  allCategoriesCityQuery,
  AllCategoriesCityResponse,
  AllCategoriesResponse,
} from './AllCategories.helpers';
import './AllCategories.scss';
import AllCategoriesCity from './AllCategoriesCity/AllCategoriesCity';
import AllCategoriesNav from './AllCategoriesNav/AllCategoriesNav';
import AllCategoriesSection from './AllCategoriesSection/AllCategoriesSection';

const AllCategories: FC = () => {
  const params = useParams<{ city?: string }>();
  const { data: allCategories, loading } = useQuery<AllCategoriesResponse, { city: string }>(allCategoriesQuery, {
    variables: {
      city: params.city ? params.city : '',
    },
  });
  const {
    data: city,
    loading: cityLoading,
    error: cityError,
  } = useQuery<AllCategoriesCityResponse, { slug: string }>(allCategoriesCityQuery, {
    variables: {
      slug: params.city ? params.city : '',
    },
    skip: !params.city,
  });
  const { currentUser } = useCurrentUser();

  const handleClick = (): void => {
    localStorage.setItem('wizardTouchPointButton', 'rdr_kategorie_link');
  };

  const renderAllCategories = () => {
    const siteTitle = t`Jakiej usługi potrzebujesz? Znajdź najlepszego wykonawca na Fixly.pl`;

    const siteDescription = t({
      id: 'all_categories_page_description',
      message:
        // eslint-disable-next-line max-len
        'Firma budowlana, ogrodnik, architekt, geodeta, a może złota rączka? Wybierz jedną z 12 kategorii dostępnych na Fixly i dodaj zlecenie. Fixly od OLX!',
    });

    return (
      <>
        <Helmet>
          <title>{siteTitle}</title>
          <meta property="og:description" content={siteDescription} />
          <meta name="msapplication-tooltip" content={siteDescription} />
          <meta name="description" content={siteDescription} />
          <link rel="canonical" href={config.SITE_URL + routes.get('categories_seo', {})} />
        </Helmet>
        <AndroidBanner />
        <div className="allCategories__back">
          <a href="/" className="allCategories__backBtn">
            <Trans>Wróć</Trans>
          </a>
        </div>

        {allCategories && (
          <div className="allCategories__content">
            <aside className="allCategories__leftNavigation">
              <div className="allCategories__leftNavigationFloatingContainer">
                <AllCategoriesNav topCategories={allCategories.topCategoryList} />
              </div>
            </aside>

            <div className="allCategories__categories">
              {allCategories.topCategoryList.map((topCategory) => (
                <AllCategoriesSection {...topCategory} key={topCategory.slug} onClick={handleClick} />
              ))}
            </div>
          </div>
        )}
      </>
    );
  };

  const renderAllCategoriesCity = () => {
    if (!allCategories || !city) return null;

    return (
      <AllCategoriesCity
        /* @ts-expect-error type mismatch */
        city={city.cityBySlug}
        allCategories={allCategories.topCategoryList}
        /* @ts-expect-error type mismatch */
        providersCounts={allCategories.homepage?.providersCounts}
      />
    );
  };

  if (loading || cityLoading) return null;

  if (cityError) {
    return <RedirectWithStatus status={301} to={routes.get('categories_seo', {})} />;
  }

  if (isWindow && currentUser && currentUser.userId) {
    // Redirect SP
    if (currentUser.isProvider) {
      window.location.href = routes.get('front::dashboard');
      return null;
    }
  }

  return (
    <div className="allCategories">
      <PageView pageLabel="browse_category" />
      <div className="allCategories__container">{city ? renderAllCategoriesCity() : renderAllCategories()}</div>
      {currentUser && <AdminMessageController />}
    </div>
  );
};

export default memo(AllCategories);
